// Thin wrapper over supabase-js auth - the only thing supabase-js does in the
// browser (§8.2). Codes, never magic links: signup and password recovery both
// resolve through an 8-digit emailed code.
import { supabase } from '@/lib/supabase';

// Consent version recorded on signup; bump when the Terms/Privacy change materially.
export const CONSENT_VERSION = '2026-07-21';

export async function signUp(fullName: string, email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        terms_accepted_at: new Date().toISOString(),
        terms_version: CONSENT_VERSION,
      },
    },
  });
  if (error) throw error;
  // Email confirmations are on, so Supabase hides an already-registered address
  // behind a decoy user with no identities instead of erroring. An unconfirmed
  // signup still comes back with its identity and gets a fresh code.
  return { alreadyRegistered: data.user?.identities?.length === 0 };
}

export async function verifySignupOtp(email: string, token: string) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'signup' });
  if (error) throw error;
}

export async function signInWithPassword(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function resendSignupCode(email: string) {
  const { error } = await supabase.auth.resend({ type: 'signup', email });
  if (error) throw error;
}

export async function requestPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email);
  if (error) throw error;
}

export async function verifyRecoveryOtp(email: string, token: string) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'recovery' });
  if (error) throw error;
}

export async function resendRecoveryCode(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email);
  if (error) throw error;
}

export async function updatePassword(password: string) {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

export async function signOut(scope: 'local' | 'global' = 'local') {
  const { error } = await supabase.auth.signOut({ scope });
  if (error) throw error;
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}
